import { useState } from 'react';
import { CheckCircle, XCircle, Send, X } from 'lucide-react';
import Button from '../../../components/ui/Button';

const BulkApprovalPanel = ({ selectedCount, onApprove, onReject, onClose }) => {
  const [action, setAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleApprove = async (autoPost = false) => {
    try {
      setLoading(true);
      await onApprove(autoPost);
      onClose();
    } catch (error) {
      console.error('Error in bulk approve:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason?.trim()) return;
    
    try {
      setLoading(true);
      await onReject(rejectionReason);
      onClose();
    } catch (error) {
      console.error('Error in bulk reject:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Bulk Actions</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          You have selected <strong>{selectedCount}</strong> response{selectedCount > 1 ? 's' : ''}. Choose an action:
        </p>

        {/* Action Selection */}
        {!action && (
          <div className="space-y-3">
            <button
              onClick={() => setAction('approve')}
              className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all"
            >
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-blue-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Approve All</p>
                  <p className="text-xs text-gray-600">Approve responses without posting</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setAction('approve-post')}
              className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all"
            >
              <div className="flex items-center gap-3">
                <Send className="w-6 h-6 text-green-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Approve & Post All</p>
                  <p className="text-xs text-gray-600">Approve and immediately post responses</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setAction('reject')}
              className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-red-500 hover:bg-red-50 transition-all"
            >
              <div className="flex items-center gap-3">
                <XCircle className="w-6 h-6 text-red-600" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">Reject All</p>
                  <p className="text-xs text-gray-600">Reject all selected responses</p>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Approve Confirmation */}
        {action === 'approve' && (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Are you sure you want to approve <strong>{selectedCount}</strong> response{selectedCount > 1 ? 's' : ''}?
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => handleApprove(false)}
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Processing...' : 'Confirm Approval'}
              </Button>
              <Button
                onClick={() => setAction(null)}
                disabled={loading}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
            </div>
          </div>
        )}

        {/* Approve & Post Confirmation */}
        {action === 'approve-post' && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                Are you sure you want to approve and immediately post <strong>{selectedCount}</strong> response{selectedCount > 1 ? 's' : ''}?
              </p>
              <p className="text-xs text-green-700 mt-2">
                ⚠️ This action will publish responses to social media platforms immediately.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => handleApprove(true)}
                disabled={loading}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Processing...' : 'Confirm & Post'}
              </Button>
              <Button
                onClick={() => setAction(null)}
                disabled={loading}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
            </div>
          </div>
        )}

        {/* Reject Confirmation */}
        {action === 'reject' && (
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 mb-3">
                Please provide a reason for rejecting <strong>{selectedCount}</strong> response{selectedCount > 1 ? 's' : ''}:
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e?.target?.value)}
                placeholder="Enter rejection reason..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleReject}
                disabled={loading || !rejectionReason?.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {loading ? 'Processing...' : 'Confirm Rejection'}
              </Button>
              <Button
                onClick={() => setAction(null)}
                disabled={loading}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkApprovalPanel;